import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import EmailInboxSection from '@/components/Settings/EmailInboxSection'
import { Analytics } from '@/analytics/events'
import { useOnboarding } from '@/hooks/useOnboarding'
import { api } from '@/api/client'
import { useSubscription } from '@/hooks/useSubscription'
import { toast } from 'sonner'

export default function SettingsPage() {
  const { user } = useUser()
  const { profile, refetch } = useOnboarding()
  const [isRestoring, setIsRestoring] = useState(false)
  const { status, plans, createCheckoutSession, createCustomerPortalSession } = useSubscription()

  const isFreeUser = !status?.tier || status.tier === 'Free'
  const corePlan = plans?.find(p => p.tier === 'Core' && !p.isFoundingPlan)

  const handleRestoreChecklist = async () => {
    setIsRestoring(true)
    try {
      await api.updateUserProfile({ onboardingDismissed: false })
      await refetch()
      toast.success('Setup checklist restored to your dashboard.')
    } catch {
      toast.error('Failed to restore checklist.')
    } finally {
      setIsRestoring(false)
    }
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account and preferences.
        </p>
      </div>

      {/* Profile */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Email</label>
            <p className="text-gray-900">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
          <div>
            <label className="label">Name</label>
            <p className="text-gray-900">{user?.fullName || 'Not set'}</p>
          </div>
        </div>
      </div>

      {/* Preferences */}
      {profile?.onboardingDismissed && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Dashboard Setup Checklist</p>
              <p className="text-sm text-gray-500">Restore the getting started guide to your dashboard.</p>
            </div>
            <button
              className="btn-secondary"
              onClick={handleRestoreChecklist}
              disabled={isRestoring}
            >
              {isRestoring ? 'Restoring...' : 'Restore'}
            </button>
          </div>
        </div>
      )}

      {/* Subscription */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{status?.planName || 'Free Plan'}</p>
            <p className="text-sm text-gray-500">
              {status?.limits.expensesUsed || 0} / {status?.limits.maxExpenses && status.limits.maxExpenses > 0 ? status.limits.maxExpenses : '∞'} expenses used
            </p>
          </div>
          <button
            className="btn-gold"
            onClick={() => {
              if (isFreeUser) {
                if (!corePlan) {
                  toast.error('Could not load plan information. Please try again.')
                  return
                }
                Analytics.upgradeStarted('Free', 'Core', 'settings-page')
                createCheckoutSession.mutate(corePlan.id, {
                  onSuccess: (url: string) => { window.location.href = url },
                  onError: () => toast.error('Failed to start checkout')
                })
              } else {
                createCustomerPortalSession.mutate(undefined, {
                  onSuccess: (url: string) => { window.location.href = url },
                  onError: () => toast.error('Failed to open billing portal')
                })
              }
            }}
            disabled={createCheckoutSession.isPending || createCustomerPortalSession.isPending}
          >
            {isFreeUser ? 'Upgrade to Core' : 'Manage Billing'}
          </button>
        </div>
      </div>

      {/* Email Inbox */}
      <EmailInboxSection />

      {/* Data Export */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Export</h2>
        <p className="text-gray-600 mb-4">
          Download all your data including expenses and receipts.
        </p>
        <button className="btn-secondary">
          Export All Data
        </button>
      </div>

      {/* Danger Zone */}
      <div className="card border-red-200">
        <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
        <p className="text-gray-600 mb-4">
          Permanently delete your account and all associated data.
        </p>
        <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
          Delete Account
        </button>
      </div>
    </div>
  )
}
